import { ChunkErrorSessionManager } from './ChunkErrorSessionManager';
import { logEvent } from './logger-utils';
import { goToErrorPage } from './utils';
import type { RetryConfig } from './RetryConfig';

// How long to wait for a queued reload to actually happen before falling back to the
// error page. Must comfortably exceed RetryConfig.reloadDelay (50ms) so a legitimately
// slow reload isn't pre-empted, but short enough to unblock a genuinely stalled session.
const RELOAD_STALL_TIMEOUT_MS = 10000;

export class ChunkErrorRecovery {
  constructor(private readonly config: RetryConfig) {}

  async handleError(error: Error): Promise<never> {
    const state = ChunkErrorSessionManager.getState();

    if (state.isReloadInProgress) {
      return this.handleReloadInProgress(error);
    }

    if (state.isPostReload) {
      return this.handlePostReloadError(error);
    }

    return this.handleFirstTimeError(error);
  }

  private handleReloadInProgress(error: Error): Promise<never> {
    logEvent({
      message: 'Reload already in progress, returning pending promise',
      level: 'info',
      extra: { error: error.message },
    });
    // Bounded fallback: a reload should fire within reloadDelay (50ms). If the page is
    // still alive after RELOAD_STALL_TIMEOUT_MS the reload stalled (or a second chunk error
    // arrived while a reload was queued) — instead of hanging this lazy promise forever,
    // navigate to the honest error page (there is no ErrorBoundary, so a bare reject would
    // blank-screen) and then reject. Mirrors handlePostReloadError.
    return new Promise<never>((_, reject) => {
      setTimeout(() => {
        logEvent({
          message: 'Reload did not complete in time, redirecting to error page',
          level: 'error',
          extra: { stalledReload: true, errorMessage: error.message },
        });
        ChunkErrorSessionManager.clearAllFlags();
        goToErrorPage(error, { hardReload: true });
        reject(error);
      }, RELOAD_STALL_TIMEOUT_MS);
    });
  }

  private handlePostReloadError(error: Error): never {
    logEvent({
      message: 'Chunk loading failed after page reload, redirecting to error page',
      level: 'error',
      extra: { postReload: true, errorMessage: error.message },
    });
    ChunkErrorSessionManager.clearAllFlags();
    goToErrorPage(error, { hardReload: true });
    throw error;
  }

  private handleFirstTimeError(error: Error): Promise<never> {
    const errorCount = ChunkErrorSessionManager.incrementErrorCount();
    
    logEvent({
      message: `Chunk loading error count: ${errorCount}/${this.config.errorLimitBeforeReload}`,
      level: 'warning',
      extra: { 
        errorCount, 
        limit: this.config.errorLimitBeforeReload, 
        errorMessage: error.message 
      },
    });

    if (errorCount >= this.config.errorLimitBeforeReload) {
      return this.triggerPageReload(errorCount);
    }
    
    throw error;
  }

  private triggerPageReload(errorCount: number): Promise<never> {
    logEvent({
      message: 'Forcing page reload due to chunk loading failures',
      level: 'error',
      extra: { errorCount, limit: this.config.errorLimitBeforeReload },
    });
    
    ChunkErrorSessionManager.markReloadInProgress();
    ChunkErrorSessionManager.markAsPostReload();
    
    setTimeout(() => {
      window.location.reload();
    }, this.config.reloadDelay);
    
    return new Promise(() => {}) as never;
  }
}